import React from 'react';
import { useNavigate } from 'react-router-dom';
import AutoRenewToggle from './AutoRenewToggle';
import { theme } from '../../config/theme';

const TIER_LABELS = { free: 'Starter', monthly: 'Monthly', yearly: 'Yearly' };
const TIER_COLORS = { free: '#64748b', monthly: '#6366f1', yearly: '#7c3aed' };

const STATUS_STYLES = {
  active: { bg: '#dcfce7', color: '#16a34a', label: 'Active' },
  past_due: { bg: '#fef3c7', color: '#d97706', label: 'Payment Due' },
  cancelled: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' },
  expired: { bg: '#f1f5f9', color: '#64748b', label: 'Expired' },
  pending: { bg: '#ede9fe', color: '#7c3aed', label: 'Pending' },
};

/**
 * @param {Object}   seller      - seller object from subscription API
 * @param {Object}   subscription - current subscription record (with retry fields)
 * @param {boolean}  compact     - compact mode for Dashboard widget
 * @param {boolean}  autoRenew   - controlled auto-renew state
 * @param {Function} onAutoRenewChange - callback when toggled
 */
const CurrentPlanBanner = ({ seller, subscription, compact = false, autoRenew, onAutoRenewChange }) => {
  const navigate = useNavigate();

  if (!seller) return null;

  const {
    tier, subscription_status, product_limit, products_used,
    subscription_end_date, auto_renew
  } = seller;

  const tierColor = TIER_COLORS[tier] || '#6366f1';
  const statusStyle = STATUS_STYLES[subscription_status] || STATUS_STYLES.pending;

  const usedPct = product_limit > 0
    ? Math.min(100, Math.round((products_used / product_limit) * 100))
    : 0;

  const daysLeft = subscription_end_date
    ? Math.max(0, Math.ceil((new Date(subscription_end_date) - new Date()) / 86400000))
    : null;

  const hasRazorpay = !!subscription?.razorpay_subscription_id;
  const isPastDue = subscription_status === 'past_due' || subscription?.status === 'past_due';

  return (
    <div style={{
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.border.light}`,
      borderRadius: theme.borderRadius.xl,
      padding: compact ? theme.spacing.lg : theme.spacing.xl,
      display: 'flex',
      flexDirection: compact ? 'row' : 'column',
      gap: theme.spacing.lg,
      alignItems: compact ? 'center' : 'stretch',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}>
      <div style={{ flex: 1 }}>
        {/* Plan name + badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: compact ? 0 : theme.spacing.sm }}>
          <span style={{ fontWeight: theme.typography.fontWeight.bold, fontSize: compact ? theme.typography.fontSize.base : theme.typography.fontSize.lg }}>
            {TIER_LABELS[tier] || tier} Plan
          </span>

          {/* Status badge */}
          <span style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, padding: '2px 10px', borderRadius: theme.borderRadius.full, fontSize: theme.typography.fontSize.xs, fontWeight: theme.typography.fontWeight.semibold }}>
            {statusStyle.label}
          </span>

          {/* Auto-renew badge on compact view */}
          {compact && (autoRenew ?? auto_renew) && (
            <span style={{ fontSize: theme.typography.fontSize.xs, color: '#16a34a', backgroundColor: '#dcfce7', padding: '2px 8px', borderRadius: theme.borderRadius.full }}>
              Auto-renews
            </span>
          )}
        </div>

        {!compact && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md, marginTop: theme.spacing.md }}>
            {/* Product usage bar */}
            {product_limit > 0 && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing.xs, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  <span>Product usage</span>
                  <span>{products_used} / {product_limit === -1 ? '∞' : product_limit}</span>
                </div>
                <div style={{ height: '6px', backgroundColor: theme.colors.gray[200], borderRadius: theme.borderRadius.full, overflow: 'hidden' }}>
                  <div style={{
                    width: `${usedPct}%`, height: '100%', borderRadius: theme.borderRadius.full,
                    transition: 'width 0.3s ease',
                    backgroundColor: usedPct >= 90 ? theme.colors.error : usedPct >= 70 ? theme.colors.warning : tierColor
                  }} />
                </div>
              </div>
            )}

            {/* Dates */}
            <div style={{ display: 'flex', gap: theme.spacing.xl, flexWrap: 'wrap' }}>
              {daysLeft !== null && (
                <div>
                  <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                    {(autoRenew ?? auto_renew) ? 'Renews on' : 'Expires on'}
                  </p>
                  <p style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: daysLeft <= 7 ? theme.colors.error : 'inherit' }}>
                    {new Date(subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    <span style={{ fontWeight: '400', color: theme.colors.text.secondary }}> ({daysLeft === 0 ? 'Today' : `${daysLeft}d left`})</span>
                  </p>
                </div>
              )}
              {subscription?.next_billing_date && (autoRenew ?? auto_renew) && (
                <div>
                  <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Next billing</p>
                  <p style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold }}>
                    {new Date(subscription.next_billing_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              )}
            </div>

            {/* Past due warning */}
            {isPastDue && subscription && (
              <div style={{
                backgroundColor: '#fef3c7', border: '1px solid #fbbf24',
                borderRadius: theme.borderRadius.md, padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm, color: '#92400e',
              }}>
                ⚠️ Payment failed (attempt {subscription.retry_count}/3).
                {subscription.next_retry_date && (
                  <> Next retry: {new Date(subscription.next_retry_date).toLocaleDateString('en-IN')}.</>
                )}
              </div>
            )}

            {/* Auto-renew toggle */}
            {tier !== 'free' && (
              <div>
                <p style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary, marginBottom: '6px' }}>Auto-Renew</p>
                <AutoRenewToggle
                  enabled={autoRenew ?? auto_renew}
                  disabled={!hasRazorpay}
                  onChange={onAutoRenewChange}
                />
                {!hasRazorpay && (
                  <p style={{ fontSize: '10px', color: theme.colors.text.secondary, marginTop: '4px' }}>
                    Available for Razorpay subscriptions only
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/seller/subscription')}
        style={{
          padding: compact ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.md} ${theme.spacing.xl}`,
          borderRadius: theme.borderRadius.lg, border: 'none',
          background: tierColor, color: '#fff',
          fontWeight: theme.typography.fontWeight.semibold, fontSize: theme.typography.fontSize.sm,
          cursor: 'pointer', flexShrink: 0,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        {subscription_status === 'expired' ? 'Renew Plan' : subscription_status === 'past_due' ? 'Update Payment' : 'Manage Plan'}
      </button>
    </div>
  );
};

export default CurrentPlanBanner;